import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';

import SharedTextFieldInput from 'components/fields/shared/TextFieldInput';
import ValidateFile from 'components/fields/shared/Message/components/ValidateFile';
import ValidateText from 'components/fields/shared/Message/components/ValidateText';

import useFormBinderDisabled from 'hooks/useFormBinderDisabled';

import { FILE_TYPE } from 'variables/constants';

import { useVehiclePurchaseDateResolver } from './hooks/useVehiclePurchaseDateResolver';
import { getHTMLId, useResolverModal } from './resolverUtils';
import type { ValidationResolverProps } from './types';

export interface Validation124ResolverProps extends ValidationResolverProps {}

const VALIDATION_ID = '124';
const FORM_STATE_KEY = 'autoPersonalForm';

const RESOLUTION_OPTIONS = [
  {
    value: 'remarks',
    label: 'Provide remarks explaining the discrepancy',
  },
  {
    value: 'file',
    label:
      'Upload supporting document (Bill of Sale, Lease Agreement, or AutoPlus Report)',
  },
];

const Validation124Resolver = ({
  section,
  itemKey: propItemKey,
  compact = false,
}: Validation124ResolverProps) => {
  const { uuid } = useParams();
  const isBinderDisabled = useFormBinderDisabled(FORM_STATE_KEY);
  const {
    isOpen: isCompactOpen,
    onOpen: onCompactOpen,
    onClose: onCompactClose,
  } = useDisclosure();

  const itemKey = propItemKey || 'global';

  const { state, handlers, fieldIds, alertMessage, getCompactAlertStatus } =
    useVehiclePurchaseDateResolver({
      validationId: VALIDATION_ID,
      section,
      itemKey,
      formStateKey: FORM_STATE_KEY,
      fileType: FILE_TYPE.TYPE_124.type,
      fileLabel:
        'Supporting document confirming purchase date (BOS/Lease or AutoPlus)',
    });

  const htmlId = getHTMLId(VALIDATION_ID, itemKey);

  const { handleModalClose } = useResolverModal(
    compact,
    Object.values(fieldIds),
    htmlId,
    onCompactOpen,
    onCompactClose,
  );

  if (!state.show) return null;

  if (compact) {
    return (
      <>
        <div id={htmlId}></div>
        <div id={fieldIds.resolution}></div>
        <div id={fieldIds.remarks}></div>
        <Tooltip placement="top" label={alertMessage}>
          <Alert status={getCompactAlertStatus()} mt="10px" fontSize="14px">
            <Button
              onClick={() => onCompactOpen()}
              w="100%"
              whiteSpace="normal"
            >
              <AlertIcon />
              Add remark
            </Button>
          </Alert>
        </Tooltip>
        <Modal
          isOpen={isCompactOpen}
          onClose={handleModalClose}
          size="4xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Vehicle Purchase Date Discrepancy</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb="10px">
                <Validation124Resolver
                  section={section}
                  itemKey={itemKey}
                  compact={false}
                />
              </Box>
              <Button
                variant="brand"
                onClick={handleModalClose}
                mb="15px"
                float="right"
              >
                Close
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Box>
      <div id={htmlId}></div>
      <div id={fieldIds.resolution}></div>
      <div id={fieldIds.remarks}></div>

      <Alert status="warning" mt="10px" fontSize="14px">
        <AlertIcon />
        <ValidationMessage message={alertMessage} />
      </Alert>

      <Box mt="15px" p="15px" bg="gray.50" borderRadius="md">
        <FormControl mb="15px" id={fieldIds.resolution} isRequired>
          <FormLabel fontWeight="semibold">
            How would you like to resolve this discrepancy?
          </FormLabel>
          <RadioGroup
            value={state.resolutionType || ''}
            onChange={handlers.handleResolutionTypeChange}
            isDisabled={isBinderDisabled}
          >
            <Stack spacing={3}>
              {RESOLUTION_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>

        {state.showRemarksField && state.remark && (
          <VStack spacing={4} align="stretch">
            <Box id={fieldIds.remarks}>
              <SharedTextFieldInput
                label="Remarks"
                value={state.remark.text || ''}
                onChange={handlers.handleRemarksChange}
                borderColor={
                  !state.remark.text || state.remark.text.trim().length === 0
                    ? 'red'
                    : 'inherit'
                }
                formStateKey={FORM_STATE_KEY}
                section={section}
                required
              />
            </Box>

            <ValidateText
              uuid={uuid}
              remark={state.remark}
              validationId={VALIDATION_ID}
              itemKey={itemKey}
              formStateKey={FORM_STATE_KEY}
              context={alertMessage}
            />
          </VStack>
        )}

        {state.showFileUpload && state.file && (
          <ValidateFile
            uuid={uuid}
            file={state.file}
            validationId={VALIDATION_ID}
            itemKey={itemKey}
            formStateKey={FORM_STATE_KEY}
          />
        )}
      </Box>
    </Box>
  );
};

export default Validation124Resolver;
